import { useState } from "react";

import { Button } from "@/components/ui/button.tsx";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { type DismissReason, MAX_DISMISS_REASON_CHARS } from "@/types/userTasks.ts";
import {
  DISMISS_REASON_LABEL,
  GIVE_REASON_SUBMIT,
  GIVE_REASON_TITLE,
  DROP_TASK,
  SNOOZE_LABEL,
  duplicateOf,
} from "@/shared/taskRowCopy.ts";

/**
 * The field for the reader's own words, and the two buttons under it.
 *
 * Inside the dialog's content, which unmounts as it closes, so the field is
 * empty every time it opens: a draft abandoned with Cancel was a decision,
 * and the next reason should not open on it.
 */
const ReasonWords = ({
  statsigName,
  onCancel,
  onGive,
}: {
  statsigName: string;
  onCancel: () => void;
  onGive: (words: string) => void;
}) => {
  const [written, setWritten] = useState("");
  // An empty answer is not a reason, and recording `other` with nothing in
  // it would read later as a reason that was given and then lost.
  const words = written.trim();
  return (
    <>
      <Textarea
        autoFocus
        aria-label={GIVE_REASON_TITLE}
        maxLength={MAX_DISMISS_REASON_CHARS}
        value={written}
        onChange={(e) => setWritten(e.target.value)}
      />
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-statsig-name={`${statsigName}-other-cancel`}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          data-statsig-name={`${statsigName}-other-give`}
          disabled={!words}
          onClick={() => onGive(words)}
        >
          {GIVE_REASON_SUBMIT}
        </Button>
      </DialogFooter>
    </>
  );
};

/**
 * Dismiss or Drop, asking why: each reason teaches detection something
 * different, so the button opens the reasons rather than acting at once.
 * Under them, the plain ways off the list — for good, or until tomorrow —
 * and last the reader's own words, for the reason none of the rows fit.
 */
export const ReasonMenu = <R extends DismissReason>({
  label,
  subject,
  statsigName,
  reasons = [],
  duplicate,
  onPick,
  onSameAs,
  onRemove,
  onLater,
  disabled,
}: {
  label: string;
  /** What the task is, which the free-text dialog shows so it is clear what is being answered. */
  subject: string;
  statsigName: string;
  /** The shades of no this menu offers. A row that offers none drops outright. */
  reasons?: readonly R[];
  /**
   * The task detection thinks this one may repeat. Offered as a reason rather
   * than a button of its own: answering "it is that one" closes this task the
   * same way the other reasons do, and differs only in what gets recorded.
   */
  duplicate?: { id: string; title: string | null } | null;
  onPick?: (reason: R, reasonText?: string) => void;
  onSameAs?: (sameAsTaskId: string) => void;
  /** Drop it with nothing recorded about why. */
  onRemove?: () => void;
  /** Put it down until tomorrow instead of for good. */
  onLater?: () => void;
  disabled: boolean;
}) => {
  // `other` is the one reason with nothing written in it yet, so it asks. Held
  // as the reason rather than a flag: the dialog answers for whichever reason
  // opened it, and closing clears it.
  const [asking, setAsking] = useState<R | null>(null);
  // `find` rather than a flag, so the handler has the reason itself to answer
  // with and needs no assertion.
  const otherReason = reasons.find((reason) => reason === "other");
  /** The reasons drawn in place; `other` is the drill-in below them, not one of them. */
  const namedReasons = reasons.filter((reason) => reason !== "other");
  /** Whether the drill-in has anything above it for its rule to divide. */
  const answersAbove =
    namedReasons.length > 0 || !!onRemove || !!onLater || !!(duplicate && onSameAs);
  const give = (words: string) => {
    if (asking) onPick?.(asking, words);
    setAsking(null);
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-statsig-name={statsigName}
          >
            {label}
            <ChevronDown className="size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {namedReasons.map((reason) => (
            <DropdownMenuItem
              key={reason}
              className="text-xs"
              data-statsig-name={`${statsigName}-${reason}`}
              onSelect={() => onPick?.(reason)}
            >
              {DISMISS_REASON_LABEL[reason]}
            </DropdownMenuItem>
          ))}
          {/* Directly under the reasons: both answer "not this one", off the
              list or just for now. */}
          {onRemove && (
            <DropdownMenuItem
              className="text-xs"
              data-statsig-name="tasks-remove"
              onSelect={onRemove}
            >
              {DROP_TASK}
            </DropdownMenuItem>
          )}
          {/* Under Drop, as the lighter version of the same answer. */}
          {onLater && (
            <DropdownMenuItem
              className="text-xs"
              data-statsig-name="tasks-later-tomorrow"
              onSelect={onLater}
            >
              {SNOOZE_LABEL}
            </DropdownMenuItem>
          )}
          {duplicate && onSameAs && (
            <DropdownMenuItem
              className="text-xs"
              data-statsig-name="tasks-triage-same-as"
              onSelect={() => onSameAs(duplicate.id)}
            >
              {duplicateOf(duplicate.title)}
            </DropdownMenuItem>
          )}
          {/* Last of all, and the only row that opens something rather than
              answering: the rule sets it apart from the answers and the chevron
              says what it does. A reader offered a blank field above the words
              takes it every time, so it waits until none of them fit. */}
          {otherReason && answersAbove && <DropdownMenuSeparator />}
          {otherReason && (
            <DropdownMenuItem
              className="text-xs"
              data-statsig-name={`${statsigName}-other`}
              onSelect={() => setAsking(otherReason)}
            >
              {DISMISS_REASON_LABEL.other}
              <ChevronRight className="ml-auto size-3.5 text-muted-foreground" />
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={asking !== null} onOpenChange={(open) => !open && setAsking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{GIVE_REASON_TITLE}</DialogTitle>
            <DialogDescription>{subject}</DialogDescription>
          </DialogHeader>
          <ReasonWords statsigName={statsigName} onCancel={() => setAsking(null)} onGive={give} />
        </DialogContent>
      </Dialog>
    </>
  );
};
